import { Flex, Form, type TableColumnType } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import find from 'lodash/find';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import keys from 'lodash/keys';
import unset from 'lodash/unset';
import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSavedColumns } from '@hooks';
import { getNormalizePathname, getSearchParams, setSearchParams } from '@utils';

import FilterConstructorControls, {
  type FilterControl,
  type FilterResetParams,
  type FilterSearchParams,
} from './FilterConstructorControls';
import * as S from './styled';

type FilterConstructorProps<TColumn> = {
  // List of controls, the order in the array is responsible for positioning
  controls: {
    left?: FilterControl[]; // Controls will be placed on the left side
    right?: FilterControl[]; // Controls will be placed on the right side
  };
  // If the filter module is outside the constructor
  filterControls?: string[];
  // Additional custom events
  actions?: ReactNode;
  // List of table columns, type transfer is indicated in the example
  columns?: TableColumnType<TColumn>[];
  // Additional parameters from external modules
  externalParameters?: FilterSearchParams | string[] | null | FilterResetParams;
  // Enabling search query params
  withSearchParams?: boolean;
  // Lay the left controls out as `divider`-separated blocks that wrap to the
  // next line as whole units instead of forcing everything onto one row.
  wrapControls?: boolean;
  // API request for re-request with filter parameters
  onRequest?: () => void;
  // Returns the values selected in the filter
  onChange: (values: FilterSearchParams) => void;
  // Returns the filtered columns
  onChangeColumns?: (columns: TableColumnType<TColumn>[]) => void;
  // Check that all parameters have been received and are ready to be passed to the request
  // Eliminates the occasional empty request
  onReady?: (status: boolean) => void;
};

/**
 * @description Universal constructor for filtering
 *
 * @example
 *
 * <FilterConstructor<TColumn>
 *   controls={{
 *     left: [
 *       {
 *         type: 'search',
 *         formName: 'term',
 *       },
 *       {
 *         type: 'range-picker',
 *         formName: 'date',
 *       },
 *       {
 *         type: 'list-types',
 *         formName: 'type',
 *       },
 *     ],
 *     right: []
 *   }}
 *   actions={<div>...</div>}
 *   onRequest={refetchApiRequest}
 *   onChange={handleChangeFilter}
 *   onReady={handleReady}
 *   withSearchParams
 * />
 *
 */

const handleCompareParams = (value: unknown, params: FilterSearchParams) =>
  keys(value).map((key) => {
    params[key] = (value as keyof typeof value)[key];
  });

const FilterConstructor = <TColumn,>({
  controls,
  actions,
  columns,
  filterControls,
  externalParameters,
  withSearchParams,
  wrapControls,
  onRequest,
  onChange,
  onChangeColumns,
  onReady,
}: FilterConstructorProps<TColumn>) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const [form] = Form.useForm();

  const [filterSearchParams, setFilterSearchParams] =
    useState<FilterSearchParams>(
      withSearchParams ? getSearchParams(search) : {}
    );

  const { newColumns, options, checkedList, setCheckedList } =
    useSavedColumns<TColumn>({
      page: getNormalizePathname(pathname),
      columns: columns || [],
    });

  useEffect(
    () => {
      if (
        filterControls &&
        (externalParameters as FilterResetParams)?.IS_RESET
      ) {
        unset(externalParameters, 'IS_RESET');

        filterControls.map((control) => {
          unset(filterSearchParams, control || '');
        });

        handleUpdateAll(filterSearchParams);
      } else {
        handleUpdateAll({
          ...filterSearchParams,
          ...(externalParameters || {}),
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [externalParameters]
  );

  useEffect(
    () => {
      if (columns) {
        onChangeColumns?.(newColumns);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkedList]
  );

  useEffect(
    () => {
      if (withSearchParams) {
        const initialSearchParams = handleInitialSearchParams();

        const rangePickerLeft = find(controls.left, {
          type: 'range-picker',
        });

        const rangePickerRight = find(controls.right, {
          type: 'range-picker',
        });

        form.setFieldsValue({ ...filterSearchParams, ...initialSearchParams });

        if (rangePickerLeft || rangePickerRight) {
          form.setFieldsValue({
            ...filterSearchParams,
            ...initialSearchParams,
            [String(rangePickerLeft?.formName || rangePickerRight?.formName)]:
              filterSearchParams.dateFrom && filterSearchParams.dateTo
                ? [
                    dayjs(filterSearchParams.dateFrom as Dayjs),
                    dayjs(filterSearchParams.dateTo as Dayjs),
                  ]
                : null,
          });
        }

        onChange?.({ ...filterSearchParams, ...initialSearchParams });
        onReady?.(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleInitialSearchParams = () => {
    const params: FilterSearchParams = {};

    for (const control of [
      ...(controls.left || []),
      ...(controls.right || []),
    ]) {
      if (control.params?.value) {
        params[control.formName!] = control.params?.value;
      }
    }

    if (!isEmpty(params)) {
      setFilterSearchParams(params);
      handleAfterAction(params);
    }

    return params;
  };

  const navigateTo = (params: FilterSearchParams) =>
    navigate(
      !isEmpty(params) ? `${pathname}?${setSearchParams(params)}` : pathname
    );

  const handleAfterAction = (params: FilterSearchParams) =>
    withSearchParams ? navigateTo(params) : onRequest?.();

  const handleUpdateAll = (params: FilterSearchParams) => {
    onChange?.(params);
    setFilterSearchParams(params);
    handleAfterAction(params);
  };

  const handleChange = (
    value: unknown,
    { formName, type, controls }: FilterControl
  ) => {
    if (
      !value ||
      (typeof value === 'object' && 'IS_RESET' in value && value.IS_RESET)
    ) {
      if (type === 'range-picker') {
        unset(filterSearchParams, 'dateFrom');
        unset(filterSearchParams, 'dateTo');
      } else {
        if (formName) {
          unset(filterSearchParams, formName);
        }
      }

      if (type === 'module-filter') {
        controls?.map((control) => {
          unset(filterSearchParams, control.formName || '');
        });
      }

      handleUpdateAll(filterSearchParams);
    } else {
      const params: FilterSearchParams = {
        ...filterSearchParams,
      };

      if (isObject(value)) {
        if (has(value, 'reportType')) {
          handleCompareParams(
            {
              ...value,
              reportType: value.reportType ? 'pl' : 'cf',
            },
            params
          );
        } else {
          handleCompareParams(value, params);
        }
      } else {
        if (formName) {
          params[formName] = value;
        }
      }

      handleUpdateAll(params);
    }
  };

  const renderControl = (control: FilterControl, index: number) => (
    <FilterConstructorControls
      key={control.type + index}
      initialParams={filterSearchParams}
      moduleParams={{
        value: checkedList,
        onChange: setCheckedList,
        options,
      }}
      onControlChange={(value) => handleChange(value, control)}
      {...control}
    />
  );

  const generateControls = (controls: FilterControl[]) =>
    controls.map((control, index) => renderControl(control, index));

  // Split controls on `divider` boundaries and render each block as its own
  // non-wrapping row, so blocks stay intact and wrap as whole units.
  const generateControlBlocks = (controls: FilterControl[]) => {
    const blocks: FilterControl[][] = [[]];

    controls.forEach((control) => {
      if (control.type === 'divider') {
        blocks.push([]);
      } else {
        blocks[blocks.length - 1].push(control);
      }
    });

    const nonEmptyBlocks = blocks.filter((block) => block.length);

    return nonEmptyBlocks.map((block, blockIndex) => (
      <Flex align="center" gap={8} key={`block-${blockIndex}`}>
        {block.map((control, index) => renderControl(control, index))}
        {blockIndex < nonEmptyBlocks.length - 1 && <S.Divider />}
      </Flex>
    ));
  };

  return (
    <S.Container>
      <Form form={form} layout="horizontal" requiredMark={false}>
        <Flex align="center" justify="space-between">
          <Flex
            align="center"
            gap={8}
            wrap={wrapControls ? 'wrap' : 'nowrap'}
          >
            {!isEmpty(controls.left) &&
              (wrapControls
                ? generateControlBlocks(controls.left || [])
                : generateControls(controls.left || []))}
          </Flex>

          <Flex align="center" gap={20}>
            <Flex align="center" gap={16}>
              {!isEmpty(controls.right) &&
                generateControls(controls.right || [])}
            </Flex>

            {actions}
          </Flex>
        </Flex>
      </Form>
    </S.Container>
  );
};

export default FilterConstructor;
